import { Link } from "react-router-dom";
import { TrendingUp, TrendingDown, Minus, ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

/**
 * Yusi Score Insight — surfaces the rule-based score-change explanation
 * coming from the backend (`scan_doc.score_change_insight`). Hidden on the
 * very first scan (insight === null/undefined).
 *
 * Props:
 *   - insight: object from scan.score_change_insight (or null)
 *   - businessId: needed for the "View Tasks" CTA
 */
export default function ScoreInsightCard({ insight, businessId }) {
  const { t, lang } = useLang();
  if (!insight) return null;

  const { previous_score, current_score, delta, direction, changed_subscores = [], short_message, microcopy, subscore_labels } = insight;
  const dir = direction || (delta > 0 ? "increased" : delta < 0 ? "decreased" : "stable");
  const labels = (subscore_labels && subscore_labels[lang]) || (subscore_labels && subscore_labels.tr) || {};
  const headline = (short_message && short_message[lang]) || (short_message && short_message.tr) || "";
  const micro = (microcopy && microcopy[lang]) || (microcopy && microcopy.tr) || "";

  const tone =
    dir === "increased"
      ? { ring: "border-emerald-500/30", chip: "text-emerald-300 bg-emerald-500/10", icon: TrendingUp }
      : dir === "decreased"
      ? { ring: "border-amber-500/30", chip: "text-amber-300 bg-amber-500/10", icon: TrendingDown }
      : { ring: "border-slate-500/30", chip: "text-slate-300 bg-slate-500/10", icon: Minus };
  const Icon = tone.icon;
  const deltaLabel = delta > 0 ? `+${delta}` : `${delta}`;

  return (
    <div
      className={`glass rounded-3xl p-6 border ${tone.ring}`}
      data-testid="score-insight-card"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tone.chip}`}>
            <Icon size={18} />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-cyan-400 font-semibold">Yusi</div>
            <div className="font-display text-lg font-semibold text-white" data-testid="insight-title">
              {t("insight.title")}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <div className="text-slate-400">
            {t("insight.prev")}: <span className="font-mono text-slate-200">{previous_score}</span>
          </div>
          <div className="text-slate-400">
            {t("insight.current")}: <span className="font-mono text-white">{current_score}</span>
          </div>
          <div
            className={`font-mono px-2 py-0.5 rounded ${tone.chip}`}
            data-testid="insight-delta"
          >
            {deltaLabel}
          </div>
        </div>
      </div>

      <p className="text-sm text-slate-200 leading-relaxed mb-4" data-testid="insight-short-message">
        {headline}
      </p>

      {changed_subscores.length > 0 && (
        <div className="mb-4">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-400 mb-2">
            {t("insight.changedSubscores")}
          </div>
          <ul className="space-y-1.5" data-testid="insight-changed-subscores">
            {changed_subscores.map((s) => {
              const sd = s.delta;
              const cls = sd > 0 ? "text-emerald-400" : sd < 0 ? "text-amber-400" : "text-slate-400";
              const sign = sd > 0 ? `+${sd}` : `${sd}`;
              return (
                <li key={s.key} className="flex items-center justify-between text-sm">
                  <span className="text-slate-300">{labels[s.key] || s.key}</span>
                  <span className="flex items-center gap-2 text-xs text-slate-400">
                    <span className="font-mono">{s.previous} → {s.current}</span>
                    <span className={`font-mono ${cls}`}>{sign}</span>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <p className="text-xs text-slate-400 leading-relaxed mb-4" data-testid="insight-microcopy">
        {micro}
      </p>

      <Link
        to={`/app/isletme/${businessId}/gorevler`}
        className="neon-btn inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm text-white font-medium"
        data-testid="insight-cta"
      >
        {t("insight.cta")} <ArrowRight size={14} />
      </Link>
    </div>
  );
}
